import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Textarea } from '../ui/textarea';
import { Checkbox } from '../ui/checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../ui/dialog';
import {
  Package, Loader2, AlertTriangle, CheckCircle, Clock,
  Lock, Eye, EyeOff
} from 'lucide-react';
import { toast } from 'sonner';

const ReturnModal = ({
  isOpen, setIsOpen, selectedSalida, form, setForm,
  saving, handleSave, updateReturnItem, formatDate,
  systemUsers = [],
  showResponsiblePassword, setShowResponsiblePassword,
  showAdminPassword, setShowAdminPassword
}) => {
  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogContent className="sm:max-w-[750px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Registrar Retorno</DialogTitle>
          <DialogDescription>
            Retorno de equipo de la salida del {selectedSalida && formatDate(selectedSalida.date)}
          </DialogDescription>
        </DialogHeader>
        
        <div className="grid gap-4 py-4">
          {selectedSalida && (
            <Card className="bg-gradient-to-r from-blue-500/20 to-blue-500/5 border-blue-500/30">
              <CardContent className="py-4">
                <div className="flex items-center gap-4">
                  <div className="p-3 rounded-full bg-blue-500/20">
                    <Package className="h-8 w-8 text-blue-400" />
                  </div>
                  <div className="flex-1">
                    <p className="text-lg font-bold text-blue-400">{selectedSalida.responsible_name}</p>
                    <p className="text-sm text-muted-foreground">Responsable que se llevo el equipo</p>
                    <div className="flex gap-4 mt-2 text-xs text-muted-foreground">
                      <span>Fecha: {formatDate(selectedSalida.date)}</span>
                      <span>{selectedSalida.items?.length || 0} equipos</span>
                      {selectedSalida.event_name && <span>{selectedSalida.event_name}</span>}
                    </div>
                  </div>
                  <Badge className="bg-blue-500/20 text-blue-400 text-lg px-4 py-2">
                    {selectedSalida.items?.reduce((acc, item) => acc + (item.quantity || 1), 0) || 0} unidades
                  </Badge>
                </div>
              </CardContent>
            </Card>
          )}

          <Card className="bg-muted/30 border-border/40">
            <CardHeader className="py-3">
              <CardTitle className="text-sm flex items-center gap-2">
                <Lock className="h-4 w-4" />
                Verificacion de Identidad
              </CardTitle>
            </CardHeader>
            <CardContent className="pb-4 space-y-3">
              <div className="flex items-center space-x-2">
                <Checkbox 
                  id="admin_override_return"
                  checked={form.admin_override}
                  onCheckedChange={(checked) => setForm({ ...form, admin_override: checked, user_password: '' })}
                />
                <Label htmlFor="admin_override_return" className="text-sm">Override de Administrador</Label>
              </div>

              {!form.admin_override ? (
                <div className="space-y-2">
                  <Label>Contrasena de <span className="text-blue-400 font-bold">{selectedSalida?.responsible_name}</span> *</Label>
                  <div className="relative">
                    <Input
                      type={showResponsiblePassword ? "text" : "password"}
                      value={form.user_password}
                      onChange={(e) => setForm({ ...form, user_password: e.target.value })}
                      placeholder="Contrasena del responsable"
                      className="pr-10"
                    />
                    <Button type="button" variant="ghost" size="icon"
                      className="absolute right-0 top-0 h-full px-3 hover:bg-transparent"
                      onClick={() => setShowResponsiblePassword(!showResponsiblePassword)}
                    >
                      {showResponsiblePassword ? <EyeOff className="h-4 w-4 text-muted-foreground" /> : <Eye className="h-4 w-4 text-muted-foreground" />}
                    </Button>
                  </div>
                  <p className="text-xs text-muted-foreground">El responsable debe confirmar la devolucion con su contrasena</p>
                </div>
              ) : (
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-2">
                    <Label>Usuario Admin *</Label>
                    <Input value={form.admin_username} onChange={(e) => setForm({ ...form, admin_username: e.target.value })} placeholder="Usuario" />
                  </div>
                  <div className="space-y-2">
                    <Label>Contrasena Admin *</Label>
                    <div className="relative">
                      <Input
                        type={showAdminPassword ? "text" : "password"}
                        value={form.admin_password}
                        onChange={(e) => setForm({ ...form, admin_password: e.target.value })}
                        placeholder="Contrasena"
                        className="pr-10"
                      />
                      <Button type="button" variant="ghost" size="icon"
                        className="absolute right-0 top-0 h-full px-3 hover:bg-transparent"
                        onClick={() => setShowAdminPassword(!showAdminPassword)}
                      >
                        {showAdminPassword ? <EyeOff className="h-4 w-4 text-muted-foreground" /> : <Eye className="h-4 w-4 text-muted-foreground" />}
                      </Button>
                    </div>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          <div className="space-y-2">
            <Label>Notas del Retorno</Label>
            <Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Observaciones del retorno..." rows={2} />
          </div>

          {/* Fase 2 — Paso 5: quien entregó el equipo al almacén se tomó del paso 4
              (Confirmar entrega al almacén). Se muestra de forma automática (solo lectura). */}
          <div className="space-y-1 rounded-md border-2 border-emerald-500/30 bg-emerald-500/5 p-3">
            <Label>Quien entregó el equipo al almacén</Label>
            {selectedSalida?.return_receipt_info?.user_name ? (
              <p className="text-sm font-semibold text-emerald-600" data-testid="return-warehouse-user-name">
                {selectedSalida.return_receipt_info.user_name}
              </p>
            ) : (
              <p className="text-xs text-amber-600">
                Aún no se ha confirmado la entrega al almacén (Paso 4). Pide que la confirmen antes de registrar el retorno.
              </p>
            )}
            <p className="text-xs text-muted-foreground">Se registró automáticamente cuando esa persona confirmó con su usuario y contraseña.</p>
          </div>

          <div className="space-y-2">
            <Label>Equipos a retornar</Label>
            <p className="text-xs text-muted-foreground mb-2">
              Para cada equipo, especifica cuantos regresan bien, con fallas o perdidos. La suma no puede exceder la cantidad original.
            </p>
            <div className="border rounded-md divide-y">
              {form.items.map((item, index) => (
                <ReturnItemRow key={index} item={item} index={index} updateReturnItem={updateReturnItem} />
              ))}
            </div>
          </div>
        </div>
        
        {form.items?.length > 0 && (
          <div className="mx-1 p-3 rounded-lg border border-border/60 bg-muted/20 space-y-2">
            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Resumen de devolucion</p>
            {form.items.map((item, idx) => {
              const qtyGood = item.qty_good ?? item.max_quantity;
              const qtyFaulty = item.qty_faulty || 0;
              const qtyLost = item.qty_lost || 0;
              const total = qtyGood + qtyFaulty + qtyLost;
              const isValid = total === item.max_quantity;
              return (
                <div key={idx} className="text-xs space-y-0.5">
                  <p className={`font-medium ${isValid ? 'text-foreground' : 'text-red-400'}`}>{item.equipment_name} ({total}/{item.max_quantity})</p>
                  <div className="flex flex-wrap gap-x-3 gap-y-0.5 pl-2 text-muted-foreground">
                    {qtyGood > 0 && <span className="text-green-400">{qtyGood} Bueno</span>}
                    {qtyFaulty > 0 && <span className="text-amber-400">{qtyFaulty} Con Falla</span>}
                    {qtyLost > 0 && <span className="text-red-400">{qtyLost} Perdido</span>}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => setIsOpen(false)}>Cancelar</Button>
          <Button 
            onClick={handleSave} 
            disabled={saving || form.items?.some(item => ((item.qty_good ?? item.max_quantity) + (item.qty_faulty || 0) + (item.qty_lost || 0)) !== item.max_quantity)} 
            className="btn-primary"
          >
            {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            Registrar Retorno
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const ReturnItemRow = ({ item, index, updateReturnItem }) => {
  const total = (item.qty_good ?? item.max_quantity) + (item.qty_faulty || 0) + (item.qty_lost || 0);
  const remaining = item.max_quantity - total;
  const isComplete = total === item.max_quantity;
  const isOver = total > item.max_quantity;

  return (
    <div className="p-4 space-y-3">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Package className="h-5 w-5 text-muted-foreground" />
          <div>
            <div className="flex items-center gap-2">
              <p className="font-medium">{item.equipment_name}</p>
              <Badge className="text-xs bg-blue-500/20 text-blue-400 border-blue-500/30">Salio en buen estado</Badge>
            </div>
            <p className="text-xs text-muted-foreground">
              Cantidad salida: <span className="font-bold text-amber-400">{item.max_quantity}</span>
              {item.location_name && <span className="ml-2">| De: {item.location_name}</span>}
            </p>
            {item.receptor_numbers && (
              <p className="text-xs text-muted-foreground font-mono">
                No. Equipo: {item.receptor_numbers}
              </p>
            )}
          </div>
        </div>
      </div>
      
      <div className="p-3 bg-muted/30 rounded-lg space-y-3">
        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <div className="flex-1 h-2 bg-muted rounded-full overflow-hidden">
              <div className={`h-full transition-all duration-300 ${isOver ? 'bg-red-500' : isComplete ? 'bg-emerald-500' : 'bg-primary'}`}
                style={{ width: `${Math.min(100, (total / item.max_quantity) * 100)}%` }}
              />
            </div>
            <span className={`text-xs font-medium min-w-[60px] text-right ${isOver ? 'text-red-400' : isComplete ? 'text-emerald-400' : 'text-muted-foreground'}`}>
              {total} / {item.max_quantity}
            </span>
          </div>
          <div className={`text-xs flex items-center gap-1 ${isOver ? 'text-red-400' : isComplete ? 'text-emerald-400' : 'text-amber-400'}`}>
            {isOver ? <><AlertTriangle className="h-3 w-3" /><span>Excede por {Math.abs(remaining)} unidad(es)</span></>
            : isComplete ? <><CheckCircle className="h-3 w-3" /><span>Distribucion completa</span></>
            : <><Clock className="h-3 w-3" /><span>Faltan {remaining} unidad(es) por asignar</span></>}
          </div>
        </div>
        
        <div className="grid grid-cols-3 gap-3">
          <div className="space-y-1">
            <Label className="text-xs flex items-center gap-1"><CheckCircle className="h-3 w-3 text-emerald-500" />Buenos</Label>
            <Input type="number" min="0" max={item.max_quantity - (item.qty_faulty || 0) - (item.qty_lost || 0)}
              value={item.qty_good === 0 ? '0' : (item.qty_good ?? item.max_quantity)}
              onChange={(e) => {
                const raw = e.target.value;
                if (raw === '') { updateReturnItem(index, 'qty_good', 0); }
                else { const maxA = item.max_quantity - (item.qty_faulty || 0) - (item.qty_lost || 0); updateReturnItem(index, 'qty_good', Math.max(0, Math.min(parseInt(raw) || 0, maxA))); }
              }} className="h-8 text-center" />
          </div>
          <div className="space-y-1">
            <Label className="text-xs flex items-center gap-1"><AlertTriangle className="h-3 w-3 text-amber-500" />Con Fallas</Label>
            <Input type="number" min="0" max={item.max_quantity - (item.qty_lost || 0)}
              value={item.qty_faulty || 0}
              onChange={(e) => {
                const raw = e.target.value;
                if (raw === '') { updateReturnItem(index, 'qty_faulty', 0); }
                else { const maxA = item.max_quantity - (item.qty_lost || 0); updateReturnItem(index, 'qty_faulty', Math.max(0, Math.min(parseInt(raw) || 0, maxA))); }
              }} className="h-8 text-center" />
          </div>
          <div className="space-y-1">
            <Label className="text-xs flex items-center gap-1"><AlertTriangle className="h-3 w-3 text-red-500" />Perdidos</Label>
            <Input type="number" min="0" max={item.max_quantity - (item.qty_faulty || 0)}
              value={item.qty_lost || 0}
              onChange={(e) => {
                const raw = e.target.value;
                if (raw === '') { updateReturnItem(index, 'qty_lost', 0); }
                else { const maxA = item.max_quantity - (item.qty_faulty || 0); updateReturnItem(index, 'qty_lost', Math.max(0, Math.min(parseInt(raw) || 0, maxA))); }
              }} className="h-8 text-center" />
          </div>
        </div>
      </div>
      
      {total > item.max_quantity && (
        <p className="text-xs text-red-400 bg-red-500/10 p-2 rounded flex items-center gap-1">
          <AlertTriangle className="h-3 w-3" />
          La suma ({total}) excede el maximo permitido ({item.max_quantity})
        </p>
      )}
      
      {((item.qty_faulty || 0) > 0 || (item.qty_lost || 0) > 0) && (
        <div className="space-y-2">
          <Label className="text-xs">Razon de fallas/perdida (obligatorio) *</Label>
          <Input value={item.fault_reason || ''} onChange={(e) => updateReturnItem(index, 'fault_reason', e.target.value)} placeholder="Describe el problema con los equipos..." />
        </div>
      )}
      
      {(item.qty_faulty || 0) > 0 && item.available_serials?.length > 0 && (
        <SerialSelector
          item={item}
          index={index}
          updateReturnItem={updateReturnItem}
          kind="faulty"
        />
      )}
      {(item.qty_lost || 0) > 0 && item.available_serials?.length > 0 && (
        <SerialSelector
          item={item}
          index={index}
          updateReturnItem={updateReturnItem}
          kind="lost"
        />
      )}
    </div>
  );
};

const KIND_CONFIG = {
  faulty: {
    label: 'CON FALLA',
    qtyKey: 'qty_faulty',
    serialsKey: 'faulty_serials',
    searchKey: 'faulty_serial_search',
    bgClass: 'bg-amber-500/5 border-amber-500/20',
    textClass: 'text-amber-400',
    hoverClass: 'hover:bg-amber-500/10',
    selectAllId: 'return-select-all-faulty',
    searchInputId: 'return-search-input-faulty',
    noIdNotice: 'Tienes 5 dias habiles para identificar y registrar el numero de serie faltante.',
  },
  lost: {
    label: 'PERDIDAS',
    qtyKey: 'qty_lost',
    serialsKey: 'lost_serials',
    searchKey: 'lost_serial_search',
    bgClass: 'bg-red-500/5 border-red-500/20',
    textClass: 'text-red-400',
    hoverClass: 'hover:bg-red-500/10',
    selectAllId: 'return-select-all-lost',
    searchInputId: 'return-search-input-lost',
    noIdNotice: 'Tienes 5 dias habiles para identificar y registrar el numero de serie faltante.',
  },
};

const SerialSelector = ({ item, index, updateReturnItem, kind = 'faulty' }) => {
  const cfg = KIND_CONFIG[kind];
  const requiredCount = item[cfg.qtyKey] || 0;
  const selectedRaw = item[cfg.serialsKey] || '';
  const selectedList = selectedRaw.split(',').map(s => s.trim()).filter(Boolean);
  const isNoId = selectedRaw.includes('No identificado');

  // Serials already taken by the OTHER kind (mutually exclusive selection)
  const otherKey = kind === 'faulty' ? 'lost_serials' : 'faulty_serials';
  const takenByOther = ((item[otherKey] || '')
    .split(',')
    .map(s => s.trim())
    .filter(s => s && s !== 'No identificado'));

  const searchBySerial = item.search_by_serial !== false;
  const searchByReceptor = item.search_by_receptor || false;
  const searchTerm = (item[cfg.searchKey] || '').toLowerCase();

  const filtered = (item.available_serials || []).filter(unit => {
    // Hide units already taken by the other condition
    if (takenByOther.includes(unit.serial_number)) return false;
    if (!searchTerm) return true;
    const matchSerial = searchBySerial && (unit.serial_number || '').toLowerCase().includes(searchTerm);
    const matchReceptor = searchByReceptor && (unit.receptor_number || '').toLowerCase().includes(searchTerm);
    return matchSerial || matchReceptor || (!searchBySerial && !searchByReceptor);
  });

  return (
    <div className={`p-3 ${cfg.bgClass} border rounded-md space-y-2`} data-testid={`serial-selector-${kind}-${index}`}>
      <div className="flex items-center justify-between">
        <Label className={`text-xs ${cfg.textClass}`}>
          Selecciona unidades {cfg.label} ({requiredCount} requeridas)
          <span className="ml-2 text-[10px] text-muted-foreground">
            seleccionadas: {selectedList.filter(s => s !== 'No identificado').length}{isNoId ? ' + Sin identificar' : ''}
          </span>
        </Label>
        <div className="flex gap-1">
          <Button type="button" variant="ghost" size="sm" className="h-5 text-[10px] px-2"
            data-testid={`${cfg.selectAllId}-${index}`}
            disabled={isNoId}
            onClick={() => {
              const next = filtered.slice(0, requiredCount).map(u => u.serial_number).join(', ');
              updateReturnItem(index, cfg.serialsKey, next);
            }}
          >Auto-llenar</Button>
          <Button type="button" variant="ghost" size="sm" className="h-5 text-[10px] px-2"
            onClick={() => updateReturnItem(index, cfg.serialsKey, '')}>Limpiar</Button>
        </div>
      </div>

      <div className="flex items-center gap-4 px-1">
        <label className="flex items-center gap-1.5 cursor-pointer">
          <Checkbox
            checked={searchBySerial}
            onCheckedChange={(checked) => updateReturnItem(index, 'search_by_serial', !!checked)}
          />
          <span className="text-xs text-muted-foreground">Buscar por No. de Serie</span>
        </label>
        <label className="flex items-center gap-1.5 cursor-pointer">
          <Checkbox
            checked={searchByReceptor}
            onCheckedChange={(checked) => updateReturnItem(index, 'search_by_receptor', !!checked)}
          />
          <span className="text-xs text-muted-foreground">Buscar por No. de Equipo</span>
        </label>
      </div>

      <Input
        placeholder={searchByReceptor && !searchBySerial ? "Buscar por número de equipo..." : searchBySerial && !searchByReceptor ? "Buscar por número de serie..." : "Buscar por serie o equipo..."}
        value={item[cfg.searchKey] || ''}
        onChange={(e) => updateReturnItem(index, cfg.searchKey, e.target.value)}
        className="h-7 text-xs"
        data-testid={`${cfg.searchInputId}-${index}`}
      />

      <div className="grid grid-cols-2 gap-2 max-h-28 overflow-y-auto border rounded p-2 bg-background/50">
        {filtered.map(unit => {
          const checked = selectedList.includes(unit.serial_number);
          const overLimit = !checked && selectedList.filter(s => s !== 'No identificado').length >= requiredCount;
          return (
            <label key={unit.id} className={`flex items-center gap-2 p-1 rounded ${cfg.hoverClass} cursor-pointer text-xs ${overLimit ? 'opacity-50' : ''}`}>
              <Checkbox
                checked={checked}
                disabled={isNoId || (overLimit && !checked)}
                onCheckedChange={(c) => {
                  const curr = selectedList.filter(s => s !== 'No identificado');
                  const next = c
                    ? [...curr, unit.serial_number]
                    : curr.filter(s => s !== unit.serial_number);
                  updateReturnItem(index, cfg.serialsKey, next.join(', '));
                }}
              />
              <span className="font-mono">{unit.serial_number}</span>
              {unit.receptor_number && <span className="text-muted-foreground text-[10px]">({unit.receptor_number})</span>}
            </label>
          );
        })}
      </div>
      <div className="pt-2 border-t border-current/20">
        <label className={`flex items-center gap-2 p-1 rounded ${cfg.hoverClass} cursor-pointer text-xs`}>
          <Checkbox
            checked={isNoId}
            onCheckedChange={(checked) => {
              if (checked) {
                updateReturnItem(index, cfg.serialsKey, 'No identificado');
                toast.info(cfg.noIdNotice, { duration: 8000 });
              } else {
                updateReturnItem(index, cfg.serialsKey, '');
              }
            }}
          />
          <span className="text-amber-400 font-medium">No identificado</span>
          <span className="text-muted-foreground text-[10px]">(tienes 5 dias para verificarlo)</span>
        </label>
      </div>
    </div>
  );
};

export default ReturnModal;
